/**
 * Regulatory Lens — a pure client-side projection over the evaluated
 * `cards[]` (ADR-0007). It only filters / highlights; it NEVER re-fetches,
 * re-evaluates, or moves a card between lifecycle lanes.
 *
 * The lifecycle 4+2 board stays the single map. This is the orthogonal
 * "regulatory dimensions" view (object / timing / market / evidence / exposure)
 * expressed as facets — it is NOT a second 4+2 and is never labelled
 * "4+2" in the UI.
 *
 * Phase 1 ships only data-trustworthy lenses. The Exposure lens (Phase 2,
 * after `deriveLegalEffect` is refined) and the Use-case lens (Phase 3,
 * the config→rule reverse view) are shown disabled rather than filtering
 * on coarse or unmodelled data. The Timing facet marks the values the
 * v0.1 mapper does not yet emit (`event_triggered` / `phase_in`).
 */

import type {
  ComplianceRequirementCard,
  RegulatoryDomain,
  ObligationTiming,
  CoverageState,
  LegalEffect,
} from "@regpulse/contracts";

export interface LensState {
  domain: Set<string>;
  timing: Set<string>;
  jurisdiction: Set<string>;
  evidence: Set<string>;
  exposure: Set<string>;
}

export type LensFacet = keyof LensState;

export function emptyLens(): LensState {
  return {
    domain: new Set<string>(),
    timing: new Set<string>(),
    jurisdiction: new Set<string>(),
    evidence: new Set<string>(),
    exposure: new Set<string>(),
  };
}

export function lensActiveCount(lens: LensState): number {
  return (
    lens.domain.size +
    lens.timing.size +
    lens.jurisdiction.size +
    lens.evidence.size +
    lens.exposure.size
  );
}

/**
 * Pure projection. Returns the subset of `cards` matching the active
 * facets (AND across facets, OR within a facet). It never mutates a card
 * and never changes `layer`, so lifecycle-lane membership is preserved —
 * the ADR-0007 "lens does not move lanes" invariant.
 */
export function applyLens(
  cards: ComplianceRequirementCard[],
  lens: LensState,
): ComplianceRequirementCard[] {
  return cards.filter((c) => {
    if (lens.domain.size && !lens.domain.has(c.regulatoryDomain)) return false;
    if (lens.timing.size && !lens.timing.has(c.obligationTiming)) return false;
    if (lens.jurisdiction.size && !lens.jurisdiction.has(c.jurisdictionCode)) return false;
    if (lens.evidence.size && !lens.evidence.has(c.coverageState)) return false;
    if (lens.exposure.size && !lens.exposure.has(c.legalEffect)) return false;
    return true;
  });
}

// 16-value closed RegulatoryDomain set (Lesson §25.7). Re-homed here from
// the former DomainFilterBar; the Object facet is the same closed set.
const DOMAINS: RegulatoryDomain[] = [
  "Type Approval",
  "General Safety",
  "Emissions & CO2",
  "Battery & Materials",
  "Cybersecurity",
  "OTA & SUMS",
  "ADAS & Automation",
  "AI Regulation",
  "Privacy & Data",
  "Charging & Energy",
  "Member-State Operation",
  "RMI & Aftersales",
  "Market Surveillance & Recall",
  "Product Liability",
  "Market Rating",
  "Ecosystem Interop",
];

const TIMINGS: ObligationTiming[] = [
  "pre_market",
  "at_market_entry",
  "post_market",
  "recurring",
  "event_triggered",
  "phase_in",
];

// Timings the v0.1 mapper (deriveObligationTiming) does not yet emit. Shown
// disabled with a note rather than as clickable chips that filter to nothing.
const TIMING_NOT_PRODUCED_V01: ReadonlyArray<ObligationTiming> = ["event_triggered", "phase_in"];

const COVERAGE_STATES: CoverageState[] = ["missing", "partial", "linked", "ready", "stale"];

// Advisory legal-effect categories (deriveLegalEffect, ADR-0007 Phase 2).
// The Exposure lens filters on these; it is not a risk score and carries no
// penalty amounts — the chips are categorical only.
const LEGAL_EFFECTS: LegalEffect[] = [
  "market_access_blocker",
  "registration_blocker",
  "in_use_restriction",
  "cost_penalty",
  "rating_penalty",
  "commercial_risk",
  "information_only",
];

function slug(v: string): string {
  return v.replace(/[^A-Za-z0-9]/g, "_");
}

function applicableCountBy<T extends string>(
  cards: ComplianceRequirementCard[],
  pick: (c: ComplianceRequirementCard) => T,
): Map<T, number> {
  const counts = new Map<T, number>();
  for (const c of cards) {
    if (c.applicabilityCategory !== "applicable") continue;
    const k = pick(c);
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  return counts;
}

interface Props {
  cards: ComplianceRequirementCard[];
  lens: LensState;
  onToggle: (facet: LensFacet, value: string) => void;
  onClear: () => void;
}

export function RegulatoryLens({ cards, lens, onToggle, onClear }: Props) {
  const domainCounts = applicableCountBy(cards, (c) => c.regulatoryDomain);
  const timingCounts = applicableCountBy(cards, (c) => c.obligationTiming);
  const evidenceCounts = applicableCountBy(cards, (c) => c.coverageState);
  const jurisdictionCounts = applicableCountBy(cards, (c) => c.jurisdictionCode);
  const exposureCounts = applicableCountBy(cards, (c) => c.legalEffect);
  // Market facet values are data-driven: the jurisdictions actually present.
  const jurisdictions = Array.from(jurisdictionCounts.keys()).sort();

  const matched = applyLens(cards, lens).filter(
    (c) => c.applicabilityCategory === "applicable",
  ).length;
  const totalApplicable = cards.filter(
    (c) => c.applicabilityCategory === "applicable",
  ).length;
  const active = lensActiveCount(lens);

  return (
    <section
      aria-labelledby="regulatory-lens-title"
      data-testid="regulatory-lens"
      style={{
        background: "var(--color-surface-elevated)",
        border: "1px solid var(--color-border)",
        borderRadius: "var(--radius-8)",
        padding: "var(--space-12)",
      }}
    >
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "var(--space-8)",
          marginBottom: "var(--space-8)",
        }}
      >
        <div>
          <h3 id="regulatory-lens-title" className="type-h3" style={{ margin: 0, fontSize: 13 }}>
            Regulatory Lens ·{" "}
            <span style={{ color: "var(--color-text-secondary)", fontWeight: 400 }}>
              object / timing / market / evidence / exposure
            </span>
          </h3>
          <p
            className="type-caption"
            style={{ margin: "var(--space-4) 0 0 0", color: "var(--color-text-secondary)" }}
          >
            Filters and highlights the same evaluated cards. It does not re-run the evaluator
            or move cards between lifecycle lanes — the lifecycle map stays the single map.{" "}
            {active === 0
              ? "No lens active; all applicable cards shown."
              : `Lens active: showing ${matched} of ${totalApplicable} applicable cards.`}
          </p>
          <p
            data-testid="lens-counting-note"
            className="type-caption"
            style={{ margin: "var(--space-4) 0 0 0", color: "var(--color-text-secondary)" }}
          >
            Counts are applicable cards for the current configuration. 0 can mean no mapped
            rules, hidden by config, needs config, or mapped elsewhere.
          </p>
        </div>
        {active > 0 && (
          <button
            data-testid="lens-clear"
            onClick={onClear}
            style={{
              fontSize: 11,
              padding: "var(--space-4) var(--space-8)",
              border: "1px solid var(--color-border-strong)",
              background: "var(--color-surface-sunken)",
              borderRadius: "var(--radius-4)",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Clear lens
          </button>
        )}
      </header>

      <div style={{ display: "grid", gap: "var(--space-12)" }}>
        <FacetRow
          facet="domain"
          title="Object · regulatory domain"
          values={DOMAINS.map((d) => ({ value: d, count: domainCounts.get(d) ?? 0 }))}
          active={lens.domain}
          onToggle={onToggle}
        />
        <FacetRow
          facet="timing"
          title="Timing · obligation timing"
          values={TIMINGS.map((t) => ({
            value: t,
            count: timingCounts.get(t) ?? 0,
            disabled: TIMING_NOT_PRODUCED_V01.includes(t),
            disabledNote: "not produced by v0.1 mapper",
          }))}
          active={lens.timing}
          onToggle={onToggle}
        />
        <FacetRow
          facet="jurisdiction"
          title="Market · jurisdiction"
          values={jurisdictions.map((j) => ({ value: j, count: jurisdictionCounts.get(j) ?? 0 }))}
          active={lens.jurisdiction}
          onToggle={onToggle}
          emptyNote="No jurisdictions on the current applicable cards."
        />
        <FacetRow
          facet="evidence"
          title="Evidence · coverage state"
          values={COVERAGE_STATES.map((s) => ({ value: s, count: evidenceCounts.get(s) ?? 0 }))}
          active={lens.evidence}
          onToggle={onToggle}
        />
        <FacetRow
          facet="exposure"
          title="Exposure · legal effect (advisory, categorical — not a risk score)"
          values={LEGAL_EFFECTS.map((e) => ({ value: e, count: exposureCounts.get(e) ?? 0 }))}
          active={lens.exposure}
          onToggle={onToggle}
        />

        {/* Future lens — disabled until its data is modelled (ADR-0007). */}
        <div
          data-testid="lens-deferred"
          style={{
            borderTop: "1px dashed var(--color-border)",
            paddingTop: "var(--space-8)",
            display: "flex",
            flexWrap: "wrap",
            gap: "var(--space-8)",
            alignItems: "center",
            fontSize: 11,
            color: "var(--color-text-disabled)",
          }}
        >
          <span style={{ color: "var(--color-text-secondary)", fontWeight: 600 }}>
            Deferred lens:
          </span>
          <span
            data-testid="lens-deferred-usecase"
            title="Ships in Phase 3 as the config→rule reverse view (ADR-0007)."
            style={deferredChip}
          >
            Use-case · Phase 3
          </span>
        </div>
      </div>
    </section>
  );
}

interface FacetValue {
  value: string;
  count: number;
  disabled?: boolean;
  disabledNote?: string;
}

function FacetRow({
  facet,
  title,
  values,
  active,
  onToggle,
  emptyNote,
}: {
  facet: LensFacet;
  title: string;
  values: FacetValue[];
  active: Set<string>;
  onToggle: (facet: LensFacet, value: string) => void;
  emptyNote?: string;
}) {
  return (
    <div data-testid={`lens-facet-${facet}`}>
      <div
        className="type-caption"
        style={{
          color: "var(--color-text-secondary)",
          fontWeight: 600,
          marginBottom: "var(--space-4)",
        }}
      >
        {title}
      </div>
      {values.length === 0 ? (
        <p
          className="type-caption"
          style={{ margin: 0, color: "var(--color-text-disabled)", fontStyle: "italic" }}
        >
          {emptyNote ?? "No values for the current configuration."}
        </p>
      ) : (
        <div
          role="group"
          aria-label={title}
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "var(--space-4)",
          }}
        >
          {values.map((v) => {
            const isActive = active.has(v.value);
            const disabled = Boolean(v.disabled);
            return (
                <button
                  key={v.value}
                  type="button"
                  aria-pressed={isActive}
                  aria-disabled={disabled}
                  disabled={disabled}
                  data-testid={`lens-chip-${facet}-${slug(v.value)}`}
                  data-facet={facet}
                  data-value={v.value}
                  data-active={isActive}
                  data-disabled={disabled}
                  title={disabled ? v.disabledNote : undefined}
                  onClick={disabled ? undefined : () => onToggle(facet, v.value)}
                  style={{
                    fontSize: 11,
                    padding: "var(--space-4) var(--space-8)",
                    border: isActive
                      ? "1px solid var(--color-accent)"
                      : disabled || v.count === 0
                        ? "1px dashed var(--color-border)"
                        : "1px solid var(--color-border-strong)",
                    background: isActive
                      ? "var(--color-accent-soft)"
                      : disabled || v.count === 0
                        ? "var(--color-surface-sunken)"
                        : "var(--color-surface)",
                    color: disabled
                      ? "var(--color-text-disabled)"
                      : v.count === 0
                        ? "var(--color-text-disabled)"
                        : "var(--color-text-primary)",
                    borderRadius: "var(--radius-full)",
                    cursor: disabled ? "not-allowed" : "pointer",
                    fontWeight: isActive ? 700 : 400,
                    whiteSpace: "nowrap",
                  }}
                >
                  {v.value}
                  <span
                    style={{
                      marginLeft: 4,
                      fontFamily: "var(--font-mono)",
                      color: "var(--color-text-secondary)",
                    }}
                  >
                    {disabled ? "n/a" : `(${v.count})`}
                  </span>
                </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const deferredChip: React.CSSProperties = {
  padding: "var(--space-4) var(--space-8)",
  border: "1px dashed var(--color-border)",
  borderRadius: "var(--radius-full)",
  background: "var(--color-surface-sunken)",
};

export { DOMAINS };
